import SpineAnimationSequence, { ISpineAnimationSkipConfig } from '@Core/Animation/SpineAnimationSequence';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { GameScene, IOdometerCheckpoint } from '..';

export interface IWinMultiplierPresentationConfig {
    spineKey: string;
    animationNames: EGameEvents[];
    skip: ISpineAnimationSkipConfig;
    active: boolean;
    startEvent: EGameEvents;
    endEvent: EGameEvents;
}

export default class WinMultiplierPresentation extends GameComponent {
    private readonly animationSequencesByMultiplier: Map<number, SpineAnimationSequence> = new Map();
    private readonly startEvent: EGameEvents;
    private readonly endEvent: EGameEvents;
    private spineWrapper: SpineWrapper;
    private currentSequence: SpineAnimationSequence | undefined;

    constructor(
        scene: GameScene,
        private readonly buildAnimationSequence: (
            multiplier: number,
            spineWrapper: SpineWrapper,
            winMultiplierPresentationConfig: IWinMultiplierPresentationConfig
        ) => SpineAnimationSequence,
        private readonly winMultiplierPresentationConfig: IWinMultiplierPresentationConfig
    ) {
        super(scene);
        this.startEvent = winMultiplierPresentationConfig.startEvent;
        this.endEvent = winMultiplierPresentationConfig.endEvent;
    }

    public onCreate(): void {
        this.spineWrapper = this.initSpine(this.winMultiplierPresentationConfig);
    }

    private initSpine(config: IWinMultiplierPresentationConfig): SpineWrapper {
        const existingSpine = this.scene.game.spines.find((spine) => spine.dataKey === config.spineKey);
        if (existingSpine) return existingSpine;
        return new SpineWrapper(this.scene, { x: this.scene.screen.width / 2, y: this.scene.screen.height / 2 }, { dataKey: config.spineKey }, true);
    }

    public async show(multiplier: number): Promise<void> {
        this.currentSequence = this.getOrBuildSequence(multiplier);
        await this.currentSequence.play();
        EventsCenter.emit(this.endEvent);
    }

    public getOdometerCheckpoint(totalWin: number, multiplier: number): IOdometerCheckpoint {
        return {
            value: 0,
            eventToEmit: this.startEvent,
            waitForEvent: this.endEvent
        };
    }

    public skip(): void {
        this.currentSequence?.skip();
    }

    private getOrBuildSequence(multiplier: number): SpineAnimationSequence {
        let sequence = this.animationSequencesByMultiplier.get(multiplier);
        if (!sequence) {
            sequence = this.buildAnimationSequence(multiplier, this.spineWrapper, this.winMultiplierPresentationConfig);
            this.animationSequencesByMultiplier.set(multiplier, sequence);
        }
        return sequence;
    }

    private getPremultipliedTotalWin(totalWin: number, multiplier: number): number {
        return totalWin / multiplier;
    }
}
